import React from 'react';
import { Link } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
import { BrandLogo } from '../components/BrandLogo';

function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center space-x-2">
            <BrandLogo className="w-9 h-9 sm:w-11 sm:h-11" testId="privacy-logo" />
            <h1 className="text-xl sm:text-2xl font-bold text-gray-800">Legacy Forge Family</h1>
          </Link>
          <Link 
            to="/" 
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
          >
            <FaArrowLeft />
            <span className="hidden sm:inline">Back to Home</span>
          </Link>
        </div>
      </header>

      {/* Content */}
      <div className="container mx-auto px-4 py-8 sm:py-12 max-w-4xl">
        <div className="bg-white rounded-xl shadow-lg p-6 sm:p-10">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">Privacy Policy</h1>
          <p className="text-gray-500 mb-8">Last updated: March 2025</p>

          <div className="prose prose-blue max-w-none space-y-6 text-gray-700">
            <section>
              <h2 className="text-xl font-bold text-gray-900 mb-3">1. Introduction</h2>
              <p>
                Legacy Forge Family ("we," "our," or "us") is committed to protecting your privacy 
                and the privacy of your family. This Privacy Policy explains how we collect, use, 
                disclose, and safeguard your information when you use our service.
              </p>
              <p className="mt-2">
                We understand that family information is deeply personal. We treat your data with 
                the utmost care and respect, implementing strong security measures to protect your 
                family's legacy.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-gray-900 mb-3">2. Information We Collect</h2>
              
              <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Information You Provide</h3>
              <ul className="list-disc pl-6 space-y-1">
                <li><strong>Account Information:</strong> Name, email address, password</li>
                <li><strong>Profile Information:</strong> Profile photos, biographical details</li>
                <li><strong>Family Information:</strong> Family names, relationships, family history</li>
                <li><strong>Content:</strong> Photos, messages, event details, timeline entries</li>
                <li><strong>Payment Information:</strong> Processed securely through Stripe; we do not store full card numbers</li>
              </ul>

              <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Information Collected Automatically</h3>
              <ul className="list-disc pl-6 space-y-1">
                <li><strong>Usage Data:</strong> Features used, pages visited, interaction patterns</li>
                <li><strong>Device Information:</strong> Browser type, operating system, device identifiers</li>
                <li><strong>Log Data:</strong> IP addresses, access times, error logs</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-bold text-gray-900 mb-3">3. How We Use Your Information</h2>
              <p>We use your information to:</p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li>Provide and maintain the Service</li>
                <li>Process your transactions and manage subscriptions</li>
                <li>Send you important notifications about your account and family activity</li>
                <li>Respond to your inquiries and provide customer support</li>
                <li>Improve and personalize your experience</li>
                <li>Ensure the security of your account and data</li>
                <li>Comply with legal obligations</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-bold text-gray-900 mb-3">4. Information Sharing</h2>
              <p>
                <strong>We do not sell your personal information.</strong> We share information only in 
                the following limited circumstances:
              </p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li><strong>Within Your Family Group:</strong> Information you share is visible to members of your family group based on their permissions</li>
                <li><strong>Service Providers:</strong> We use trusted third parties for email delivery (Resend), payment processing (Stripe), and hosting</li>
                <li><strong>Legal Requirements:</strong> When required by law, subpoena, or to protect our rights and safety</li>
                <li><strong>With Your Consent:</strong> When you explicitly authorize sharing</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-bold text-gray-900 mb-3">5. Data Security</h2>
              <p>
                We implement robust security measures to protect your information:
              </p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li>Encryption of data in transit (HTTPS) and at rest</li>
                <li>Secure password hashing</li>
                <li>Regular security audits and updates</li>
                <li>Access controls and authentication requirements</li>
                <li>Secure cloud infrastructure</li>
              </ul>
              <p className="mt-2">
                While we strive to protect your information, no method of transmission over the 
                internet is 100% secure. We encourage you to use strong passwords and keep your 
                login credentials confidential.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-gray-900 mb-3">6. Data Retention</h2>
              <p>
                We retain your information for as long as your account is active or as needed to 
                provide you services. We understand that family memories are irreplaceable, so:
              </p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li>We provide grace periods before any data restrictions due to non-payment</li>
                <li>We will never delete your family data without significant advance notice</li>
                <li>You can request a full export of your data at any time</li>
                <li>After account deletion, we retain backups for a limited period for recovery purposes</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-bold text-gray-900 mb-3">7. Your Rights</h2>
              <p>You have the right to:</p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li><strong>Access:</strong> Request a copy of your personal data</li>
                <li><strong>Correction:</strong> Update or correct inaccurate information</li>
                <li><strong>Deletion:</strong> Request deletion of your account and data</li>
                <li><strong>Export:</strong> Receive your data in a portable format</li>
                <li><strong>Opt-out:</strong> Unsubscribe from non-essential communications</li>
              </ul>
              <p className="mt-2">
                To exercise these rights, please contact us through the application or our support channels.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-gray-900 mb-3">8. Children's Privacy</h2>
              <p>
                Legacy Forge Family allows family members to add proxy profiles for children under 13. 
                These profiles are managed by adult family members and do not require the child to 
                have an account or provide personal information directly.
              </p>
              <p className="mt-2">
                We do not knowingly collect personal information directly from children under 13. 
                If you believe a child has provided us information without parental consent, please 
                contact us immediately.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-gray-900 mb-3">9. Cookies and Tracking</h2>
              <p>
                We use essential cookies to maintain your session and provide the Service. We do not 
                use advertising cookies or sell data to advertisers. Analytics are used solely to 
                improve the Service.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-gray-900 mb-3">10. Third-Party Services</h2>
              <p>Our Service integrates with:</p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li><strong>Stripe:</strong> For secure payment processing</li>
                <li><strong>Resend:</strong> For email delivery</li>
                <li><strong>Google:</strong> For optional Google Sign-In authentication</li>
              </ul>
              <p className="mt-2">
                These services have their own privacy policies. We encourage you to review them.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-gray-900 mb-3">11. International Data Transfers</h2>
              <p>
                Your information may be stored and processed in the United States or other countries 
                where our service providers operate. We ensure appropriate safeguards are in place 
                for any international transfers.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-gray-900 mb-3">12. Changes to This Policy</h2>
              <p>
                We may update this Privacy Policy from time to time. We will notify you of significant 
                changes via email or through the Service. The "Last updated" date at the top reflects 
                the most recent revision.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-gray-900 mb-3">13. Contact Us</h2>
              <p>
                If you have questions or concerns about this Privacy Policy or our data practices, 
                please contact us through:
              </p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li>The feedback button within the application</li>
                <li>Email: family@legacyforge.family</li>
              </ul>
            </section>
          </div>

          <div className="mt-10 pt-6 border-t border-gray-200 flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between">
            <Link 
              to="/" 
              className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium"
            >
              <FaArrowLeft />
              Back to Home
            </Link>
            <Link 
              to="/terms" 
              className="text-gray-600 hover:text-gray-900"
            >
              View Terms of Service
            </Link>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-800 text-white py-6 mt-8">
        <div className="container mx-auto px-4 text-center">
          <p className="text-gray-400 text-sm">
            &copy; 2025 Legacy Forge Family. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}

export default PrivacyPolicy;
